import { Injectable, inject } from '@angular/core';
import { LocationService } from './location.service';
import type { HttpHeaders } from '@angular/common/http';

/**
 * Used at application startup to handle session on application startup before configuration is available
 */

@Injectable({
  providedIn: 'root'
})
export class PreBootSessionService {
  protected readonly locationService = inject<LocationService>(LocationService);

  protected redirectToLoginScreenInProgress: boolean = false;

  /**
   *
   * Check if user session has timed out using UIP redirect header "Ajax-Response"
   *
   * @param responseHeaders - headers will be checked for redirection
   * @returns true if the user session has timed out, false otherwise.
   */
  hasSessionTimedOut(responseHeaders: HttpHeaders): boolean {
    return this.isAjaxRedirect(responseHeaders);
  }

  /**
   * Redirect the user to the login page
   */
  redirectToLoginPage(headers: HttpHeaders): void {
    if (!this.redirectToLoginScreenInProgress) {
      this.locationService.getCurrentLocation().replace(this.getRedirectUrl(headers));
      this.redirectToLoginScreenInProgress = true;
    }
  }

  protected getLoginUrl(redirectLocation?: string): string {
    return redirectLocation !== undefined ? redirectLocation : '/';
  }

  protected isAjaxRedirect(headers: HttpHeaders): boolean {
    return headers !== undefined && headers.get('Ajax-Response') === 'redirect';
  }

  protected getRedirectUrlFromLocation(locationHeader: string, indexPageUrl: string, stateUrl: string, searchString: string): string {
    // fixing goto url for UAP - it is incorrect because it contains rest call's url in the goto param
    // we will be replacing it with current application's url keeping UAP's URL
    let uapUrl = this.getUAPUrl(locationHeader);
    if (uapUrl !== undefined) {
      uapUrl += this.getUAPParams(locationHeader, indexPageUrl, stateUrl, searchString);
    }
    return uapUrl;
  }

  protected getLocationHeader(headers: HttpHeaders): string {
    return headers.get('Location');
  }

  protected getRedirectUrl(headers: HttpHeaders): string {
    const locationHeader = this.getLocationHeader(headers);
    const indexPageUrl = this.locationService.getIndexPageUrl();
    const stateUrl = this.locationService.getHash();
    const searchString = this.locationService.getSearch();
    const url = this.getRedirectUrlFromLocation(locationHeader, indexPageUrl, stateUrl, searchString);
    return this.getLoginUrl(url);
  }

  /**
   * Strip's query string containing incorrect goto param
   *
   * @param locationHeader
   */
  private getUAPUrl(locationHeader: string): string {
    return (locationHeader !== undefined && locationHeader !== null && locationHeader !== '') ?
      locationHeader.split('?')[0] : undefined;
  }

  private getUAPParams(location: string, indexPageUrl: string, stateUrl: string, searchString: string): string {
    if (location !== undefined && location.trim() !== '') {
      const searchParams = new URLSearchParams(location.slice(location.indexOf('?')));
      if (searchParams.has('goto')) {
        searchParams.delete('goto');
      }
      return '?' + searchParams.toString() +
              '&' + this.getGotoUrl(indexPageUrl, stateUrl, searchString);
    }
    return '';
  }

  private getGotoUrl(indexPageUrl: string, stateUrl: string, searchString: string): string {
    const params = new URLSearchParams(searchString);
    if (stateUrl !== undefined && stateUrl !== null && stateUrl.trim().length > 0) {
      params.append('hash', stateUrl);
    }
    const gotoUrl = `${indexPageUrl}?${params.toString()}`;
    return indexPageUrl !== undefined && indexPageUrl.trim() !== '' ? `goto=${gotoUrl}` : '';
  }
}
